import { Component, computed, effect, inject, input, model, output, signal } from "@angular/core";
import { AssertionIssue, Media } from "../../../../core/database/db";
import { ModalIcon, UiModalService } from "../../../../shared/components/modal/ui-modal.service";
import { StandardBottomSheetComponent } from "../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { SectionHeaderComponent } from "../../../../shared/components/section-header/section-header.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { IssueCardComponent } from "../issue-card/issue-card.component";
import { IssueConfirmationComponent } from "../issue-confirmation/issue-confirmation.component";
import { InputErrorComponent } from "../../../../shared/components/input-error/input-error.component";

/** A carried-over previous-step issue plus its photos (Media), ready to render in the stepper. */
export interface PreviousIssueCard {
  issue: AssertionIssue;
  media: Media[];
}

type StepView = "list" | "confirm";

/**
 * Bottom-sheet stepper that walks an inspector through the still-open issues carried over from the
 * preceding BQ step on an assertion (e.g. the CM's BQ6 self-inspection findings during a CCM BQ7 audit).
 *
 * Step 1 ("list"): the open carried-over issues are shown as selectable cards. "Next" carries the checked
 * issues forward and dismisses the unchecked ones. Steps 2..N ("confirm"): each checked issue is confirmed
 * with a required severity; confirming clones it into a current-step issue (handled by the host). This
 * component is presentational — it emits intent and never touches IndexedDB directly.
 */
@Component({
  selector: "app-previous-issue-container",
  standalone: true,
  imports: [
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    VerticalButtonGroupComponent,
    IssueCardComponent,
    IssueConfirmationComponent,
    InputErrorComponent
  ],
  templateUrl: "./previous-issue-container.component.html",
  styleUrl: "./previous-issue-container.component.scss"
})
export class PreviousIssueContainerComponent {
  /** Two-way open state of the sheet. */
  isOpen = model<boolean>(false);
  /** Assertion text shown as context on the list step. */
  assertionText = input<string>("");
  /** The still-open carried-over issues (with resolved thumbnails) to review. */
  issues = input<PreviousIssueCard[]>([]);

  /**
   * Emitted once, at the end of the stepper, with every issue the CCM picked a severity for. The host
   * persists these as current-step issues (parentIdentifier = the carried-over issue). Nothing is emitted
   * mid-flow, so cancelling before the end saves nothing.
   */
  readonly confirmIssues = output<{ issue: AssertionIssue; severity: string }[]>();
  /** Emitted with the carried-over issues the inspector chose not to bring forward (left unchecked), applied at the end. */
  readonly dismissIssues = output<AssertionIssue[]>();
  /** Emitted when the CCM wants to add a brand-new issue (host opens the needs-attention flow). */
  readonly addNewIssue = output<void>();
  /** Emitted once the CCM has stepped all the way through the workflow for this assertion. */
  readonly completed = output<void>();

  private readonly modalService = inject(UiModalService);

  protected readonly step = signal<StepView>("list");
  protected readonly selectedIdentifiers = signal<Set<string>>(new Set<string>());
  protected readonly confirmQueue = signal<PreviousIssueCard[]>([]);
  /** Unchecked issues staged for dismissal; only applied when the CCM saves at the end. */
  protected readonly dismissedQueue = signal<PreviousIssueCard[]>([]);
  protected readonly currentIndex = signal<number>(0);
  protected readonly severityByIdentifier = signal<Record<string, string>>({});

  protected readonly currentCard = computed<PreviousIssueCard | undefined>(() => this.confirmQueue()[this.currentIndex()]);
  protected readonly isLastConfirmation = computed(() => this.currentIndex() === this.confirmQueue().length - 1);
  protected readonly currentSeverity = computed(() => {
    const card = this.currentCard();
    return card ? this.severityByIdentifier()[card.issue.identifier] ?? "" : "";
  });
  /** Host-driven submit-attempt flag so the confirmation step only reddens after a Next without a severity. */
  protected readonly showConfirmErrors = signal(false);
  /** Set when the CCM taps Next on the list step without selecting any issue to carry forward. */
  protected readonly showListError = signal(false);

  /** 0 = list step; otherwise the 1-based index of the active confirm panel in the sliding track. */
  protected readonly position = computed(() => (this.step() === "list" ? 0 : this.currentIndex() + 1));
  /** Horizontal track offset: Next slides the next panel in from the right, Back reverses it. */
  protected readonly trackTransform = computed(() => `translateX(-${this.position() * 100}%)`);

  protected readonly footerButtons = computed<VerticalButton[]>(() =>
    (this.step() === "list" ? this.buildListButtons() : this.buildConfirmButtons())
  );

  constructor() {
    // Reset the stepper each time the sheet opens so a fresh assertion starts from the list step.
    effect(() => {
      if (this.isOpen()) {
        this.resetState();
      }
    });
  }

  protected isSelected(card: PreviousIssueCard): boolean {
    return this.selectedIdentifiers().has(card.issue.identifier);
  }

  protected onSelectionChange(card: PreviousIssueCard, selected: boolean): void {
    this.selectedIdentifiers.update(current => {
      const next = new Set(current);
      if (selected) {
        next.add(card.issue.identifier);
      } else {
        next.delete(card.issue.identifier);
      }
      return next;
    });

    if (selected) {
      this.showListError.set(false);
    }
  }

  protected onCardSeverityChange(card: PreviousIssueCard, severity: string): void {
    this.severityByIdentifier.update(current => ({ ...current, [card.issue.identifier]: severity }));
    if (severity) {
      this.showConfirmErrors.set(false);
    }
  }

  protected onBack(): void {
    this.showConfirmErrors.set(false);
    if (this.currentIndex() > 0) {
      this.currentIndex.update(index => index - 1);
    } else {
      this.step.set("list");
    }
  }

  private buildListButtons(): VerticalButton[] {
    return [
      {
        label: "Next",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => this.onListNext(),
        testId: "previous-issue-container-list-next"
      },
      {
        label: "Add New Issue",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        disabled: false,
        action: () => this.onAddNewIssue(),
        testId: "previous-issue-container-list-add-new"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.onCancel(),
        testId: "previous-issue-container-list-cancel"
      }
    ];
  }

  private buildConfirmButtons(): VerticalButton[] {
    const buttons: VerticalButton[] = [];

    if (this.isLastConfirmation()) {
      buttons.push({
        label: "Save & Return to List",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => this.onSaveAndReturn(),
        testId: "previous-issue-container-confirm-save"
      });
      buttons.push({
        label: "Save & Add New Issue",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.onSaveAndAddNewIssue(),
        testId: "previous-issue-container-confirm-add-new"
      });
    } else {
      buttons.push({
        label: "Next",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => this.onConfirmNext(),
        testId: "previous-issue-container-confirm-next"
      });
    }

    buttons.push({
      label: "Cancel",
      severity: "secondary",
      text: true,
      styleClass: "bqa-secondary-btn",
      action: () => this.onCancel(),
      testId: "previous-issue-container-confirm-cancel"
    });

    return buttons;
  }

  private onListNext(): void {
    const selected = this.selectedIdentifiers();
    const checked = this.issues().filter(card => selected.has(card.issue.identifier));

    // At least one issue must be carried forward; otherwise flag the required-selection error and wait.
    if (checked.length === 0) {
      this.showListError.set(true);
      return;
    }

    // Stage the unchecked issues for dismissal; nothing is applied until the CCM saves at the end.
    const unchecked = this.issues().filter(card => !selected.has(card.issue.identifier));
    this.dismissedQueue.set(unchecked);

    this.showListError.set(false);
    this.showConfirmErrors.set(false);
    this.confirmQueue.set(checked);
    this.currentIndex.set(0);
    this.step.set("confirm");
  }

  private onConfirmNext(): void {
    if (!this.currentSeverity()) {
      this.showConfirmErrors.set(true);
      return;
    }
    // The severity choice is already staged in severityByIdentifier; just advance. Nothing is persisted
    // until the CCM saves on the last issue.
    this.showConfirmErrors.set(false);
    this.currentIndex.update(index => index + 1);
  }

  private onSaveAndReturn(): void {
    if (!this.currentSeverity()) {
      this.showConfirmErrors.set(true);
      return;
    }
    this.showConfirmErrors.set(false);
    this.commitStagedWork();
    this.completed.emit();
    this.close();
  }

  private onSaveAndAddNewIssue(): void {
    if (!this.currentSeverity()) {
      this.showConfirmErrors.set(true);
      return;
    }
    this.showConfirmErrors.set(false);
    this.commitStagedWork();
    this.addNewIssue.emit();
    this.close();
  }

  private onAddNewIssue(): void {
    // Jumping straight to a brand-new issue from the list step saves no carryover work.
    this.addNewIssue.emit();
    this.close();
  }

  protected onCancel(): void {
    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to cancel?",
      message: "Any work you've done up to this point will be lost.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.close(),
      isDestructive: true
    });
  }

  /**
   * Emits every staged confirmation (issues the CCM picked a severity for) and the staged dismissals in a
   * single pass, so the host persists the BQ7 issues only now — at the end of the stepper.
   */
  private commitStagedWork(): void {
    const confirmations = this.confirmQueue().map(card => ({
      issue: card.issue,
      severity: this.severityByIdentifier()[card.issue.identifier] ?? ""
    }));
    this.confirmIssues.emit(confirmations);

    const dismissed = this.dismissedQueue();
    if (dismissed.length > 0) {
      this.dismissIssues.emit(dismissed.map(card => card.issue));
    }
  }

  private close(): void {
    this.isOpen.set(false);
  }

  private resetState(): void {
    this.step.set("list");
    this.selectedIdentifiers.set(new Set<string>());
    this.confirmQueue.set([]);
    this.dismissedQueue.set([]);
    this.currentIndex.set(0);
    this.severityByIdentifier.set({});
    this.showConfirmErrors.set(false);
    this.showListError.set(false);
  }
}
